import React, { useRef, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { DeepChat } from "deep-chat-react";
import { useTranslation } from "react-i18next";
import { ExclamationCircleOutlined } from "@ant-design/icons";
import { useFetch } from "../../hooks/fetch";
import ACTIONS from "../../store/action";
import ReactDOMServer from "react-dom/server";
import ErrorMessage from "../customComponent/customMessages/ErrorMessage";
import {
  DeepChatStyle,
  DeepChatMessagesStyle,
  DeepChatTextInputPlaceholderStyle,
  DeepChatTextInputContainerStyle,
  DeepChatSubmitButtonLoadingStyle,
  DeepChatSubmitButtonStyle
} from "./Styles";
import { registerMessageActionsWebComponent } from "./MessageActions";
import { sendMessageToAIChatBot, getAIChatBotConversationHistory, createAIChatBotConversation } from "../../api/api";
import { IntroPanel } from "./IntroPanel";
import {
  NEW_CHAT_ID,
  CHAT_ROLES,
  USER_MESSAGE_CHARACTER_LIMIT,
  MAX_USER_MESSAGES_TO_SEND_TO_SERVER,
  PENDO_EVENTS
} from "./Constants";
import SubmitButton from "../../assets/svg/aiChatBotSubmit.js";
import { useDashboardBucketsTrackingEvent } from "../../utils/pendo/hooks";

const getHtmlContent = (conversationId, message) => {
  // Encode message content to prevent HTML injection issues
  const encodedContent = encodeURIComponent(message.text);
  return `<message-actions message-id="${message.id}" message-content="${encodedContent}" conversation-id="${conversationId}" message-feedback=${message.feedback}></message-actions>`;
};

const getHtmlErrorMessage = (t) => {
  const errorMessage = t("ai_chat_bot.errors.failed_processing_request");
  const iconHtml = ReactDOMServer.renderToString(<ExclamationCircleOutlined />);

  const errorHtml = `<div style="text-align: center; line-height: 150%; display: flex; align-items: flex-start;">
    <span style="width: auto; font-size: 18px; margin-right: 8px; line-height: 150%;">${iconHtml}</span>
    <span style="width: auto; font-size: 16px; line-height: 150%;">${errorMessage}</span>
  </div>`;

  return errorHtml;
};

const ChatWidgetComponent = ({
  selectedConversationId,
  shouldLoadHistory,
  clearChatHistory,
  handleAfterConversationCreated,
  handleAfterLoadingSelectedConversation,
  onChatCleared
}) => {
  const user = useSelector((state) => state.user);
  const userId = user?.oktaId;
  const dispatch = useDispatch();
  const isIntroPanelShown = useSelector((state) => state.aiChatBot.isIntroPanelShown);
  const { t } = useTranslation();
  const chatElementRef = useRef();
  const conversationCreatedRef = useRef(false);
  const [getConversationHistory, conversationHistoryResult, , conversationHistoryError] = useFetch(
    getAIChatBotConversationHistory
  );
  const { trackEvent } = useDashboardBucketsTrackingEvent();

  // Store conversation ID in a ref instead of state to avoid re-renders
  const activeConversationIdRef = useRef(selectedConversationId);
  const chatKey = activeConversationIdRef?.current ?? NEW_CHAT_ID;

  const scrollToBottom = useCallback(() => {
    const shadowRoot = chatElementRef.current?.shadowRoot;
    if (!shadowRoot) {
      return;
    }

    let messagesContainer = null;

    const elements = shadowRoot.querySelectorAll('div[style*="height"]');
    for (const element of elements) {
      if (element.scrollHeight > element.clientHeight && !element.scrollTop) {
        messagesContainer = element;
        break;
      }
    }

    if (messagesContainer) {
      messagesContainer.scrollTop = Math.min(messagesContainer.scrollTop + 100, messagesContainer.scrollHeight);

      let parent = messagesContainer.parentElement;
      while (parent && parent !== shadowRoot) {
        if (!parent.scrollTop) {
          parent.scrollTop = Math.min(parent.scrollTop + 100, parent.scrollHeight);
        }
        parent = parent.parentElement;
      }
    }
  }, []);

  useEffect(() => {
    window.scrollChatToBottom = scrollToBottom;
    return () => {
      delete window.scrollChatToBottom;
    };
  }, [scrollToBottom]);

  useEffect(() => {
    // Update the ref when URL param changes
    if (selectedConversationId && Number(selectedConversationId) !== Number(activeConversationIdRef.current)) {
      activeConversationIdRef.current = selectedConversationId;
    }
  }, [selectedConversationId]);

  useEffect(() => {
    registerMessageActionsWebComponent(); // Register Web Component on mount
  }, []);

  useEffect(() => {
    if (chatElementRef.current && selectedConversationId && shouldLoadHistory) {
      getConversationHistory(userId, selectedConversationId);
      activeConversationIdRef.current = selectedConversationId;
    }
  }, [shouldLoadHistory, selectedConversationId]);

  useEffect(() => {
    const isNewConversationUrl = location.pathname.includes("new");
    if (!isNewConversationUrl) {
      dispatch(ACTIONS.setAiChatBotIntroPanelShown(false));
    }
  }, []);

  useEffect(() => {
    if (chatElementRef.current && clearChatHistory) {
      chatElementRef.current.clearMessages();
      activeConversationIdRef.current = null;
      conversationCreatedRef.current = false;
      onChatCleared();
    }
  }, [clearChatHistory, onChatCleared]);

  useEffect(() => {
    if (conversationHistoryError) {
      ErrorMessage(t("ai_chat_bot.errors.failed_fetching_conversation_history"));
    }
  }, [conversationHistoryError]);

  useEffect(() => {
    let observer;
    const interval = setInterval(() => {
      const shadowRoot = chatElementRef.current?.shadowRoot;
      if (!shadowRoot) return;

      const input = shadowRoot.querySelector("#input");
      if (input) {
        input.style.visibility = isIntroPanelShown ? "hidden" : "visible";
      }

      observer = new MutationObserver(() => {
        const input = shadowRoot.querySelector("#input");
        if (input) {
          input.style.visibility = isIntroPanelShown ? "hidden" : "visible";
        }
      });

      observer.observe(shadowRoot, {
        childList: true,
        subtree: true
      });

      clearInterval(interval);
    }, 50);

    return () => {
      clearInterval(interval);
      if (observer) observer.disconnect();
    };
  }, [isIntroPanelShown]);

  useEffect(() => {
    if (conversationHistoryResult) {
      chatElementRef.current.loadHistory = () => {
        const conversationId = activeConversationIdRef.current;
        const messages = conversationHistoryResult.data.messages || [];
        return messages.map((msg) => {
          if (msg.role === CHAT_ROLES.AI) {
            const html = getHtmlContent(conversationId, msg);
            return {
              role: msg.role,
              text: msg.text,
              html
            };
          }
          return msg;
        });
      };
      handleAfterLoadingSelectedConversation();
    }
  }, [conversationHistoryResult]);

  const handleSuggestedQuestionClick = useCallback(
    (questionText) => {
      trackEvent(PENDO_EVENTS.INTRO_PANEL_OPTION_SELECTED, { questionText });
      dispatch(ACTIONS.setAiChatBotIntroPanelShown(false));
      if (chatElementRef.current) {
        setTimeout(() => {
          chatElementRef.current.submitUserMessage({ text: questionText });
        }, 100);
      }
    },
    [dispatch]
  );

  const createConversationId = async () => {
    try {
      const result = await createAIChatBotConversation();
      return result.data;
    } catch (error) {
      trackEvent(PENDO_EVENTS.TEAMMATE_AI_ERROR_MESSAGE, {
        type: "conversationId creation error",
        message: error.message
      });
      ErrorMessage(t("ai_chat_bot.errors.failed_creating_conversation"));
    }
  };

  const sendMessage = useCallback(
    async (body, signals) => {
      let currentConversationId = activeConversationIdRef.current;
      let conversationCreated = false;
      try {
        if (!currentConversationId && !conversationCreatedRef.current) {
          currentConversationId = await createConversationId();
          if (currentConversationId) {
            activeConversationIdRef.current = currentConversationId;
            conversationCreatedRef.current = true;
            conversationCreated = true;
          } else {
            trackEvent(PENDO_EVENTS.TEAMMATE_AI_ERROR_MESSAGE, {
              type: "conversationId is undefined"
            });
            signals.onResponse({
              role: "ai_error",
              html: getHtmlErrorMessage(t)
            });
            return;
          }
        }

        const lastMessage = body.messages[0];
        trackEvent(PENDO_EVENTS.QUESTION_SUBMITTED, { conversationId: currentConversationId });
        const response = await sendMessageToAIChatBot(currentConversationId, lastMessage);

        // we call it after the response received to make sure conversation subject is set
        if (conversationCreated) {
          handleAfterConversationCreated(currentConversationId);
        }
        signals.onOpen();

        const abortController = new AbortController();
        signals.stopClicked.listener = () => {
          abortController.abort();
        };

        try {
          const reader = response.body.getReader();
          const decoder = new TextDecoder();
          let buffer = "";
          let messageId = null;
          let feedback = null;

          // eslint-disable-next-line no-constant-condition
          while (true) {
            if (abortController.signal.aborted) {
              break;
            }

            const { done, value } = await reader.read();
            if (done) {
              break;
            }

            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split("\n");
            buffer = lines.pop();

            for (const line of lines) {
              if (line.startsWith("data: ")) {
                try {
                  const data = JSON.parse(line.slice(6));

                  if (data.text !== undefined) {
                    signals.onResponse({
                      text: data.text,
                      overwrite: data.overwrite || false
                    });
                  }
                  if (data.messageId) {
                    messageId = data.messageId;
                    feedback = data.feedback;
                  }
                  if (data.error) {
                    signals.onResponse({ error: data.error });
                    return;
                  }
                } catch (parseError) {
                  trackEvent(PENDO_EVENTS.TEAMMATE_AI_ERROR_MESSAGE, {
                    type: "Error parsing SSE data",
                    message: parseError.message,
                    messageId,
                    conversationId: currentConversationId
                  });
                }
              }
            }
          }

          const html = getHtmlContent(currentConversationId, {
            text: "", // The text is already displayed by streaming
            id: messageId,
            feedback: feedback
          });

          signals.onResponse({
            html,
            overwrite: true // Replace the last message to add HTML
          });

          signals.onClose();

          trackEvent(PENDO_EVENTS.ANSWER_DISPLAYED, { answerId: messageId, conversationId: currentConversationId });
        } catch (streamError) {
          trackEvent(PENDO_EVENTS.TEAMMATE_AI_ERROR_MESSAGE, {
            type: "Stream error",
            message: streamError.message,
            conversationId: currentConversationId
          });
          signals.onResponse({ error: streamError.message });
          signals.onClose();
        }
      } catch (error) {
        if (conversationCreated) {
          handleAfterConversationCreated(currentConversationId);
        }

        trackEvent(PENDO_EVENTS.TEAMMATE_AI_ERROR_MESSAGE, {
          type: "server error",
          message: error.message,
          conversationId: currentConversationId
        });

        const errorHtml = getHtmlErrorMessage(t);

        signals.onResponse({
          role: "ai_error",
          html: errorHtml
        });
      }
    },
    [handleAfterConversationCreated, clearChatHistory, user]
  );

  return user ? (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        alignItems: "center",
        justifyContent: "center"
      }}
    >
      <DeepChat
        style={DeepChatStyle}
        ref={chatElementRef}
        key={chatKey}
        connect={{ handler: sendMessage, stream: true }}
        requestBodyLimits={{
          maxMessages: MAX_USER_MESSAGES_TO_SEND_TO_SERVER
        }}
        onError={(error) => {
          trackEvent(PENDO_EVENTS.TEAMMATE_AI_ERROR_MESSAGE, {
            type: "DeepChat error",
            message: error
          });
        }}
        errorMessages={{
          displayServiceErrorMessages: true
        }}
        messageStyles={DeepChatMessagesStyle}
        submitButtonStyles={{
          alwaysEnabled: true,
          loading: {
            container: {
              default: DeepChatSubmitButtonStyle
            }
          },
          submit: {
            container: {
              default: DeepChatSubmitButtonLoadingStyle
            },
            svg: {
              content: SubmitButton
            }
          }
        }}
        textInput={{
          styles: {
            container: DeepChatTextInputContainerStyle
          },
          placeholder: {
            text: t("ai_chat_bot.message_placeholder"),
            style: DeepChatTextInputPlaceholderStyle
          },
          characterLimit: USER_MESSAGE_CHARACTER_LIMIT
        }}
      >
        <IntroPanel onSuggestedQuestionClick={handleSuggestedQuestionClick} />
      </DeepChat>
    </div>
  ) : null;
};

export { ChatWidgetComponent as ChatWidget };
